/**
 * Locale registry — the single source of truth for which UI languages the
 * application knows about (P3 F9 multi-language architecture).
 *
 * ---------------------------------------------------------------------------
 * HOW TO ADD A NEW UI LANGUAGE (e.g. German):
 *   1. Create `src/locales/de/` with the same JSON namespace files as `en`.
 *   2. Add a `KNOWN_LOCALES` entry below (code, names, Intl tag, date-fns key).
 *   3. (Optional) enable it for operators in Settings → Languages, or seed it
 *      into `general.enabled_locales` on the backend.
 * That's it — `i18n.ts` discovers the folder via `import.meta.glob` and the
 * formatters / type union / settings UI all derive from this registry.
 * ---------------------------------------------------------------------------
 *
 * Two distinct concepts live here:
 *   - KNOWN locales: every locale the codebase ships translation assets +
 *     formatter mappings for. Bounded by what `import.meta.glob` can discover.
 *   - ENABLED locales: the subset an operator has switched on in Settings.
 *     Hydrated from the backend `general.enabled_locales` setting at runtime;
 *     defaults to all known locales before hydration so behaviour never breaks.
 */

/** A locale the application ships assets and formatter mappings for. */
export interface KnownLocale {
  /** Base language code used as the i18n + JSONB map key (`en`, `es`). */
  code: string;
  /** English display name. */
  name: string;
  /** Native display name (shown in the language switcher). */
  nativeName: string;
  /** BCP-47 Intl locale tag for `Intl.*` formatting (e.g. `en-GB`). */
  intlLocale: string;
  /** date-fns locale import key (matches an export from `date-fns/locale`). */
  dateFnsKey: string;
}

/**
 * Every locale the codebase knows about. To add a language, drop a
 * `src/locales/<code>/` folder AND add a one-line entry here.
 */
export const KNOWN_LOCALES: readonly KnownLocale[] = [
  { code: 'en', name: 'English', nativeName: 'English', intlLocale: 'en-GB', dateFnsKey: 'enUS' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', intlLocale: 'es-ES', dateFnsKey: 'es' },
] as const;

/** All known locale codes. */
export const KNOWN_LOCALE_CODES = KNOWN_LOCALES.map((l) => l.code);

/** Hardcoded baseline default + fallback (overridable via backend settings). */
export const DEFAULT_LOCALE_CODE = 'en';
export const FALLBACK_LOCALE_CODE = 'en';

/** Lookup a known locale definition by base code (region-stripped). */
export function getKnownLocale(code: string): KnownLocale | undefined {
  const base = code.split('-')[0]?.toLowerCase();
  return KNOWN_LOCALES.find((l) => l.code === base);
}

// ---------------------------------------------------------------------------
// Enabled-locale runtime registry (hydrated from backend settings).
// ---------------------------------------------------------------------------

let enabledCodes: string[] = [...KNOWN_LOCALE_CODES];
let defaultCode: string = DEFAULT_LOCALE_CODE;

/**
 * Hydrate the enabled-locale registry from the backend `general.*` settings.
 * Unknown codes are ignored (we can only render locales we ship assets for).
 * Falls back to all known locales when the setting is empty/invalid so the
 * UI never ends up with zero languages.
 */
export function hydrateEnabledLocales(input: {
  enabled?: string[] | null;
  default?: string | null;
}): void {
  const filtered = (input.enabled ?? [])
    .map((c) => c.split('-')[0]?.toLowerCase() ?? '')
    .filter((c) => KNOWN_LOCALE_CODES.includes(c));

  enabledCodes = filtered.length > 0 ? Array.from(new Set(filtered)) : [...KNOWN_LOCALE_CODES];

  const desiredDefault = (input.default ?? '').split('-')[0]?.toLowerCase() ?? '';
  defaultCode = enabledCodes.includes(desiredDefault)
    ? desiredDefault
    : enabledCodes[0] ?? DEFAULT_LOCALE_CODE;
}

/** Currently-enabled locale codes (subset of known). */
export function getEnabledLocaleCodes(): string[] {
  return [...enabledCodes];
}

/** Currently-enabled locale definitions, in registry order. */
export function getEnabledLocales(): KnownLocale[] {
  return KNOWN_LOCALES.filter((l) => enabledCodes.includes(l.code));
}

/** The operator-configured default locale code. */
export function getDefaultLocaleCode(): string {
  return defaultCode;
}
